import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const TOKEN_KEY = "akyvia.admin.token";
const EMAIL_KEY = "akyvia.admin.email";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState(() => localStorage.getItem(EMAIL_KEY));
  const [checking, setChecking] = useState(true);

  // Attach interceptor once
  useEffect(() => {
    const id = api.interceptors.request.use((cfg) => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (t && !cfg.headers?.Authorization) {
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${t}`;
      }
      return cfg;
    });
    return () => api.interceptors.request.eject(id);
  }, []);

  // Verify token on mount
  useEffect(() => {
    let cancelled = false;
    const verify = async () => {
      if (!token) {
        setChecking(false);
        return;
      }
      try {
        await api.get("/auth/me");
      } catch (e) {
        if (!cancelled) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(EMAIL_KEY);
          setToken(null);
          setEmail(null);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    verify();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = useCallback(async (emailInput, password) => {
    const { data } = await api.post("/auth/login", {
      email: emailInput,
      password,
    });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(EMAIL_KEY, data.email);
    setToken(data.access_token);
    setEmail(data.email);
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
    setToken(null);
    setEmail(null);
  }, []);

  const value = useMemo(
    () => ({ token, email, isAuthenticated: !!token, checking, login, logout }),
    [token, email, checking, login, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
